import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import posthog from 'posthog-js';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/directory',
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/pages/LoginPage.vue'),
    meta: { guest: true },
  },
  {
    path: '/activate',
    name: 'ActivateAccount',
    component: () => import('@/pages/ClaimAccountPage.vue'),
    meta: { guest: true },
  },
  {
    path: '/claim',
    redirect: '/activate',
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/pages/ForgotPasswordPage.vue'),
    meta: { guest: true },
  },
  {
    path: '/callback',
    name: 'Callback',
    component: () => import('@/pages/CallbackPage.vue'),
  },
  {
    path: '/rsvp',
    name: 'Rsvp55th',
    component: () => import('@/pages/RsvpPage.vue'),
    meta: { public: true },
  },
  {
    path: '/directory',
    name: 'MemberDirectory',
    component: () => import('@/pages/MemberDirectoryPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/members',
    redirect: '/directory',
  },
  {
    path: '/members/:id',
    name: 'MemberDetail',
    component: () => import('@/pages/MemberDetailPage.vue'),
    meta: { requiresAuth: true },
    props: true,
  },
  {
    path: '/members/:id/edit',
    name: 'MemberEdit',
    component: () => import('@/pages/MemberEditPage.vue'),
    meta: { requiresAuth: true },
    props: true,
  },
  {
    path: '/pledge-class/:name',
    name: 'PledgeClass',
    component: () => import('@/pages/PledgeClassPage.vue'),
    meta: { requiresAuth: true },
    props: true,
  },
  {
    path: '/stats',
    name: 'ChapterStats',
    component: () => import('@/pages/MemberDemographicsPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/my-era',
    name: 'MyEra',
    component: () => import('@/pages/MyEraPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/officers',
    name: 'Officers',
    component: () => import('@/pages/OfficersPage.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Gated by the PostHog 'family-tree' feature flag — the page itself
    // redirects to /directory when the flag is off (see FamilyTreePage).
    path: '/family-tree',
    name: 'FamilyTree',
    component: () => import('@/pages/FamilyTreePage.vue'),
    meta: { requiresAuth: true },
  },
  {
    path: '/profile',
    name: 'Profile',
    component: () => import('@/pages/ProfilePage.vue'),
    meta: { requiresAuth: true },
  },
  {
    // Path must stay exactly '/giving' — the backend's Stripe Checkout
    // Session success_url/cancel_url hardcode this path (see
    // checkout-session-handler.ts, docs/1855-club-stripe-plan.md Phase 5).
    path: '/giving',
    name: 'Giving',
    component: () => import('@/pages/GivingPage.vue'),
    // No feature-flag gate on the route itself — any authenticated member
    // who knows this URL can reach it directly. '1855-club-redesign-preview'
    // only controls whether the nav link to it is shown (NavBarAlt.vue's
    // `givingEnabled`), not page access. (A page-level flag redirect used to
    // live here and in GivingPage — removed because it bounced real beta
    // testers on direct URL access, and access was never supposed to be
    // flag-gated in the first place, only the nav link's visibility was.)
    meta: { requiresAuth: true },
  },
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('@/pages/AdminDashboardPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/insights',
    name: 'AdminInsights',
    component: () => import('@/pages/AdminInsightsPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/members/new',
    name: 'AdminCreateMember',
    component: () => import('@/pages/AdminCreateMemberPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/members/import',
    name: 'AdminBulkImport',
    component: () => import('@/pages/AdminBulkImportPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/members/graduate',
    name: 'AdminGraduateMembers',
    component: () => import('@/pages/AdminGraduateMembersPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/officers/transition',
    name: 'AdminOfficerTransition',
    component: () => import('@/pages/AdminOfficerTransitionPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/audit',
    name: 'AuditLog',
    component: () => import('@/pages/AuditLogPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/email-activity',
    name: 'AdminEmailActivity',
    component: () => import('@/pages/AdminEmailActivityPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/treasurer',
    name: 'Treasurer',
    component: () => import('@/pages/TreasurerPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/club1855-reconciliation',
    name: 'AdminClub1855Reconciliation',
    component: () => import('@/pages/AdminClub1855ReconciliationPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/export',
    name: 'Export',
    component: () => import('@/pages/ExportPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/reference',
    name: 'AdminReferenceData',
    component: () => import('@/pages/AdminReferenceDataPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: () => import('@/pages/AdminUsersPage.vue'),
    meta: { requiresAuth: true, requiresSuperAdmin: true },
  },
  {
    path: '/admin/rsvp-report',
    name: 'AdminRsvpReport',
    component: () => import('@/pages/AdminRsvpReportPage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/admin/guide',
    name: 'AdminGuide',
    component: () => import('@/pages/AdminGuidePage.vue'),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/pages/NotFoundPage.vue'),
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Navigation guards
// Uses the return-based Vue Router 4 API (not the next(route) callback style) to avoid
// Vue Router internally creating a NavigationRedirect error that Sentry would capture as noise.
router.beforeEach(async (to, _from) => {
  const authStore = useAuthStore();

  // Ensure auth state is loaded
  if (!authStore.isInitialized) {
    await authStore.checkAuth();
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const requiresAdmin = to.matched.some((record) => record.meta.requiresAdmin);
  const requiresSuperAdmin = to.matched.some((record) => record.meta.requiresSuperAdmin);
  const isGuestRoute = to.matched.some((record) => record.meta.guest);

  if (requiresAuth && !authStore.isAuthenticated) {
    return { name: 'Login', query: { redirect: to.fullPath } };
  }

  if (requiresSuperAdmin && !authStore.isSuperAdmin) {
    return { name: 'Admin' };
  }

  if (requiresAdmin && !authStore.isAdmin) {
    return { name: 'MemberDirectory' };
  }

  // Routes that use a feature flag to gate *access* (not just a nav link's
  // visibility) check it themselves on mount instead of here — e.g.
  // FamilyTreePage's onFeatureFlags + fail-closed timeout pattern.
  // posthog.isFeatureEnabled() reads false on a fresh page load until
  // PostHog's flags request resolves, so a synchronous check in this guard
  // would bounce real users on direct URL access.

  if (isGuestRoute && authStore.isAuthenticated) {
    return { name: 'MemberDirectory' };
  }
});

// Stale chunk reload: after a deploy, hashed JS/CSS filenames change. Any user who
// had the app open will get a failed dynamic import when navigating to a new route.
// Catch those errors and do a hard reload to the target path so they get fresh chunks.
router.onError((error, to) => {
  const isChunkError =
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Importing a module script failed') ||
    error.message.includes('Unable to preload') ||
    error.message.includes('is not a valid JavaScript MIME type');

  if (isChunkError) {
    window.location.assign(to.fullPath);
  }
});

// Track page views on every navigation
router.afterEach((to) => {
  posthog.capture('$pageview', {
    $current_url: window.location.href,
    route_name: to.name,
    path: to.path,
  });
});

export default router;
